import React, { useState } from 'react';
import { API_BASE, ANALYTICS, waLink, SITE } from '@/config';
import { getUtm, getLandingUrl, trackEvent } from '@/lib/tracking';
import { WhatsAppIcon } from '@/components/CTAButtons';
import { Phone, CheckCircle2, Loader2 } from 'lucide-react';

const CONFIG_OPTIONS = ['1 BHK', '2 BHK', '2.5 BHK', 'Not Sure'];

const LeadForm = ({ source = 'enquiry', testidPrefix = 'lead-form', compact = false }) => {
  const [form, setForm] = useState({ name: '', mobile: '', email: '', configuration: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = 'Please enter your name (min 2 characters).';
    const digits = form.mobile.replace(/\D/g, '').replace(/^(91|0)(?=[6-9]\d{9}$)/, '');
    if (!/^[6-9]\d{9}$/.test(digits)) e.mobile = 'Enter a valid 10-digit Indian mobile number.';
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Enter a valid email address.';
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus('saving');
    const payload = {
      ...form,
      source,
      page_url: window.location.href,
      landing_url: getLandingUrl(),
      timestamp: new Date().toISOString(),
      ...getUtm(),
    };
    try {
      const res = await fetch(`${API_BASE}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      if (ANALYTICS.LEAD_ENDPOINT) {
        fetch(ANALYTICS.LEAD_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).catch(() => {});
      }
      trackEvent(source === 'popup' ? 'lead_popup_submit' : 'enquiry_submit', { configuration: form.configuration });
      sessionStorage.setItem('ppv_popup_done', '1');
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="text-center py-6" data-testid={`${testidPrefix}-thankyou`}>
        <CheckCircle2 className="h-12 w-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-display text-3xl text-palm font-medium">Thank You!</h3>
        <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto leading-relaxed">
          Your request for Provident Palm Vista has been received. Our team will get in touch with you shortly.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-thankyou-whatsapp`}
            onClick={() => trackEvent('whatsapp_click', { from: 'thankyou' })}
            className="inline-flex items-center gap-2 rounded-full bg-[#1FAF54] text-white px-6 py-3 text-sm font-semibold hover:bg-[#178f43] transition-colors">
            <WhatsAppIcon /> Chat on WhatsApp
          </a>
          <a href={SITE.tel} data-testid={`${testidPrefix}-thankyou-call`}
            onClick={() => trackEvent('phone_click', { from: 'thankyou' })}
            className="inline-flex items-center gap-2 rounded-full bg-palm text-ivory px-6 py-3 text-sm font-semibold hover:bg-palm-2 transition-colors">
            <Phone className="h-4 w-4" /> Call Now
          </a>
        </div>
      </div>
    );
  }

  const inputCls = (bad) =>
    `w-full rounded-xl border ${bad ? 'border-red-400' : 'border-line'} bg-white px-4 py-3 text-sm text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition`;

  return (
    <form onSubmit={submit} noValidate data-testid={testidPrefix} className={compact ? 'space-y-3' : 'space-y-4'}>
      <div>
        <input data-testid={`${testidPrefix}-name-input`} type="text" placeholder="Name *" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls(errors.name)} aria-label="Name" />
        {errors.name && <p className="text-xs text-red-500 mt-1" data-testid={`${testidPrefix}-name-error`}>{errors.name}</p>}
      </div>
      <div>
        <input data-testid={`${testidPrefix}-mobile-input`} type="tel" placeholder="Mobile Number *" value={form.mobile}
          onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inputCls(errors.mobile)} aria-label="Mobile Number" />
        {errors.mobile && <p className="text-xs text-red-500 mt-1" data-testid={`${testidPrefix}-mobile-error`}>{errors.mobile}</p>}
      </div>
      <div>
        <input data-testid={`${testidPrefix}-email-input`} type="email" placeholder="Email (optional)" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls(errors.email)} aria-label="Email" />
        {errors.email && <p className="text-xs text-red-500 mt-1" data-testid={`${testidPrefix}-email-error`}>{errors.email}</p>}
      </div>
      <div>
        <select data-testid={`${testidPrefix}-config-select`} value={form.configuration}
          onChange={(e) => setForm({ ...form, configuration: e.target.value })} aria-label="Configuration"
          className={`${inputCls(false)} ${form.configuration ? '' : 'text-muted-foreground/60'}`}>
          <option value="">Configuration (optional)</option>
          {CONFIG_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {status === 'error' && (
        <p className="text-xs text-red-500" data-testid={`${testidPrefix}-error`}>Something went wrong. Please try again or reach us on WhatsApp.</p>
      )}
      <button type="submit" disabled={status === 'saving'} data-testid={`${testidPrefix}-submit-button`}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-palm px-6 py-3.5 text-sm font-bold shadow-md hover:brightness-110 transition-all disabled:opacity-60">
        {status === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
        Get Project Details
      </button>
      <p className="text-[11px] text-muted-foreground/80 text-center leading-relaxed">
        By submitting, you agree to be contacted about Provident Palm Vista. Your details stay private.
      </p>
    </form>
  );
};

export default LeadForm;
